import React, { useEffect, useState } from "react";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle, FileEdit } from "lucide-react";
import { getSessionUsername } from "../../utils/sessionUser";

const OpeningBalanceList = ({ setView, setEditingContext }) => {
  const { user } = useAuth();
  const [balances, setBalances] = useState([]);
  const [filteredBalances, setFilteredBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("card");

  // Fetch data from API
  useEffect(() => {
    const fetchBalances = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.OpenningBalanceList(user?.UsrName || user?.username || getSessionUsername());
        const table = response?.Table || [];

        const formatted = table.map((item) => ({
          id: item.Cpid,
          vendor: `${item.Party} (${item.VCode})`,
          invNo: item.InvNo,
          invDate: item.InvDate ? item.InvDate.split("T")[0] : "",
          amount: item.NetTotal,
          notes: item.Notes,
        }));

        setBalances(formatted);
        setFilteredBalances(formatted);
      } catch (err) {
        console.error("Error fetching balances:", err);
        setError("Failed to load opening balances");
      } finally {
        setLoading(false);
      }
    };

    fetchBalances();
  }, [user?.UsrName, user?.username]);

  // Filter balances on search
  useEffect(() => {
    const filtered = balances.filter((item) => {
      const term = searchTerm.toLowerCase();
      return (
        item.vendor?.toLowerCase().includes(term) ||
        item.invNo?.toLowerCase().includes(term) ||
        item.invDate?.toLowerCase().includes(term) ||
        item.amount?.toString().includes(term) ||
        item.notes?.toLowerCase().includes(term)
      );
    });
    setFilteredBalances(filtered);
  }, [searchTerm, balances]);

  const handleEdit = (balance) => {
    setEditingContext(balance);
    setView("openingBalanceEdit");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header Section */}
      <div className="px-3 py-3 bg-grey border-b">
        <div className="mb-3">
          <Heading>Opening Balance List</Heading>
        </div>

        {/* New + View Toggle */}
        <div className="flex items-center gap-2 mb-3">
          <Button
            className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
                       rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
            onClick={() => {
              setEditingContext(null);
              setView("openingBalanceForm");
            }}
          >
            <PlusCircle size={18} />
            <span className="font-medium">New</span>
          </Button>

          <div className="flex-1" />

          {/* Grid / Table Toggle */}
          <div className="flex items-center justify-center p-1 bg-white-100 rounded-lg">
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
              </svg>
            </button>

            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Back + Search */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
            onClick={() => setView("list")}
          >
            ⬅
          </Button>

          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by party, invoice no, date, amount..."
          />
        </div>
      </div>

      {/* Data Section */}
      <div className="overflow-x-auto p-3">
        {loading ? (
          <p className="text-center text-gray-500 py-4">Loading...</p>
        ) : error ? (
          <p className="text-center text-red-500 py-4">{error}</p>
        ) : filteredBalances.length === 0 ? (
          <p className="text-center text-gray-500 py-4">No records found</p>
        ) : viewMode === "table" ? (
          <table className="min-w-full border border-black-200">
            <thead className="bg-gray-100 text-black-700">
              <tr>
                <th className="px-4 py-2 border text-center">Slno</th>
                <th className="px-4 py-2 border text-left">Party</th>
                <th className="px-4 py-2 border text-left">Inv No</th>
                <th className="px-4 py-2 border text-center">Inv Date</th>
                <th className="px-4 py-2 border text-right">Amount</th>
                <th className="px-4 py-2 border text-left">Notes</th>
                <th className="px-4 py-2 border text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBalances.map((item, index) => (
                <tr key={item.id}>
                  <td className="px-4 py-2 border text-center">{index + 1}</td>
                  <td className="px-4 py-2 border text-left">{item.vendor}</td>
                  <td className="px-4 py-2 border text-left">{item.invNo}</td>
                  <td className="px-4 py-2 border text-center">{item.invDate}</td>
                  <td className="px-4 py-2 border text-right">{item.amount}</td>
                  <td className="px-4 py-2 border text-left">{item.notes}</td>
                  <td className="px-4 py-2 border text-center">
                    <Button
                      type="button"
                      onClick={() => handleEdit(item)}
                       className="flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-white 
                          bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
                          hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
                          transition-all duration-300 ease-in-out"
                    >
                      <FileEdit className="w-5 h-5" /> Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // Card / Grid view
           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
  {filteredBalances.map((item) => (
    <Card key={item.id} className="p-0 bg-white border rounded-lg shadow-sm hover:shadow-md transition-all">
      
      <div className="text-sm text-gray-700 mb-1"><strong>Party:</strong> {item.vendor}</div>
      <div className="text-sm text-gray-700 mb-1"><strong>Inv No:</strong> {item.invNo}</div>
      <div className="text-sm text-gray-700 mb-1"><strong>Date:</strong> {item.invDate}</div>
      <div className="text-sm text-gray-700 mb-1"><strong>Amount:</strong> ₹{Number(item.amount).toFixed(2)}</div>
      
      <Button
        type="button"
        onClick={() => handleEdit(item)}
        className="mt-2 w-full flex items-center justify-center gap-2 px-2 py-1 text-xs font-medium text-white
                   bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-sm
                   hover:from-blue-600 hover:to-indigo-700 hover:shadow-md transition-all duration-200"
      >
        <FileEdit className="w-4 h-4" /> Edit
      </Button>
    </Card>
  ))}
</div>

        )}
      </div>
    </div>
  );
};

export default OpeningBalanceList;
